import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Viagra100mg() {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(150);
  const [selectedStrength, setSelectedStrength] = useState('100mg');

  const strengths = [
    { value: '100mg', label: '100mg', link: '/products/viagra-100mg' },
    { value: '150mg', label: '150mg', link: '/products/viagra-150mg' },
    { value: '200mg', label: '200mg', link: '/products/viagra-200mg' }
  ];

  const packages = [
    { pills: 150, price: 120, pricePerPill: 0.80 },
    { pills: 200, price: 150, pricePerPill: 0.75 },
    { pills: 300, price: 200, pricePerPill: 0.67 }
  ];

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          const cart = JSON.parse(savedCart);
          const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
          setCartCount(count);
        }
      } catch (error) {
        console.error('Error reading cart from localStorage:', error);
        setCartCount(0);
      }
    };
    updateCartCount();
  }, []);

  const handleStrengthChange = (link: string) => {
    navigate(link);
  };

  const product = {
    id: 101,
    name: 'Viagra 100mg',
    category: 'ED Treatment',
    images: [
      'https://public.readdy.ai/ai/img_res/da7be8b289114ebde4190445bda44557.jpg',
      'https://readdy.ai/api/search-image?query=viagra%20100mg%20tablets%20blister%20pack%20detailed%20view%20professional%20pharmaceutical%20photography%20clean%20white%20background%20medical%20product%20shot&width=600&height=600&seq=102&orientation=squarish'
    ],
    rating: 4.8,
    reviewCount: 542,
    inStock: true,
    description: 'Viagra (Sildenafil) 100mg is a prescription medication used to treat erectile dysfunction (ED) in men. It works by increasing blood flow to the penis to help achieve and maintain an erection during sexual stimulation.',
    dosage: 'Take one tablet approximately 30-60 minutes before sexual activity. Do not take more than one dose per day.',
    sideEffects: 'Common side effects may include headache, flushing, upset stomach, nasal congestion, and dizziness.',
    manufacturer: 'Pfizer',
    activeIngredient: 'Sildenafil Citrate 100mg'
  };

  const addToCart = () => {
    try {
      const selectedPkg = packages.find((pkg) => pkg.pills === selectedPackage);
      if (!selectedPkg) {
        alert('Invalid package selected.');
        return;
      }
      const savedCart = localStorage.getItem('cart');
      const cart = savedCart ? JSON.parse(savedCart) : [];
      
      cart.push({
        id: `${product.id}-${selectedPackage}`,
        name: `${product.name} - ${selectedPkg.pills} pills`,
        category: product.category,
        price: selectedPkg.price,
        image: product.images[0],
        quantity: quantity
      });
      
      localStorage.setItem('cart', JSON.stringify(cart));
      navigate('/cart');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</Link>
            <div className="flex items-center space-x-4">
              <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img src={product.images[0]} alt={product.name} className="w-full h-96 object-cover object-top rounded-lg shadow-lg" />
          </div>

          <div>
            <span className="text-sm text-teal-600 font-semibold">{product.category}</span>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{product.name}</h1>

            <div className="flex items-center mb-6 mt-4">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-lg`}></i>
                ))}
              </div>
              <span className="ml-2 text-gray-600">({product.reviewCount} reviews)</span>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Strength</label>
              <div className="flex gap-3">
                {strengths.map((strength) => (
                  <button
                    key={strength.value}
                    onClick={() => handleStrengthChange(strength.link)}
                    className={`px-6 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedStrength === strength.value
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {strength.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Package</label>
              <div className="space-y-3">
                {packages.map((pkg) => (
                  <div
                    key={pkg.pills}
                    onClick={() => setSelectedPackage(pkg.pills)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedPackage === pkg.pills ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-900">{pkg.pills} Pills</p>
                        <p className="text-sm text-gray-600">${(pkg.price / pkg.pills).toFixed(2)} per pill</p>
                      </div>
                      <p className="text-2xl font-bold text-teal-600">${pkg.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer">
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer">
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <button onClick={addToCart} className="flex-1 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-semibold">
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Add to Cart - ${((packages.find((pkg) => pkg.pills === selectedPackage)?.price || 0) * quantity).toFixed(2)}
                </button>
              </div>
            </div>

            <div className="border-t pt-6">
              <p className="text-sm text-gray-600"><strong>SKU:</strong> MED{product.id.toString().padStart(6, '0')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 MediCare. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
